import { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabase.js';
import {
  count, countValue, formatDay, formatMoney, jobKindLabel, jobKinds, jobStatusLabel, jobStatuses, listJobs, listRowLimit, loadAllRows, orDash, showTrades, workStatusLabel,
} from './adminData.js';
import { useSort } from './SortableTable.jsx';
import { labelOf } from './sorting.js';

const noRows = [];
const sortBy = {
  job: { value: (row) => row.title },
  kind: { value: (row) => labelOf(row.kind, jobKindLabel) },
  postedBy: { value: (row) => row.posted_by },
  area: { value: (row) => row.location },
  status: { value: (row) => labelOf(row.status, jobStatusLabel) },
  bids: { value: (row) => countValue(row.bids), first: 'desc' },
  posted: { value: (row) => row.created_at, first: 'desc' },
  deadline: { value: (row) => row.deadline, first: 'desc' },
  awardedTo: { value: (row) => row.awarded_to },
  work: { value: (row) => labelOf(row.work_status, workStatusLabel) },
  value: { value: (row) => row.amount_minor, first: 'desc' },
};

/** Every job on the site: home jobs, commercial jobs and private tenders. */
export default function JobsTab({ onOpen }) {
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [typed, setTyped] = useState('');
  const [query, setQuery] = useState('');
  const [list, setList] = useState({ rows: null, total: 0, capped: false, error: '' });
  const latest = useRef(0);
  const { rows, header } = useSort(list.rows || noRows, sortBy);

  // Search 300 ms after typing stops.
  useEffect(() => {
    const timer = setTimeout(() => setQuery(typed.trim()), 300);
    return () => clearTimeout(timer);
  }, [typed]);

  // Reads the whole list, 200 jobs at a time. Changing a filter starts again, and only the newest request is kept.
  useEffect(() => {
    const ticket = ++latest.current;
    loadAllRows((page) => listJobs(supabase, { kind, status, query, ...page }), { isCurrent: () => ticket === latest.current, identify: (row) => `${row.kind}-${row.id}` })
      .then((all) => {
        if (all && ticket === latest.current) setList({ rows: all.rows, total: all.total, capped: all.capped, error: '' });
      })
      .catch((cause) => {
        if (ticket === latest.current) setList((current) => ({ rows: current.rows || [], total: current.total, capped: current.capped, error: cause.message }));
      });
    return () => { latest.current += 1; };
  }, [kind, status, query]);

  if (!list.rows) return <p>Loading jobs…</p>;
  const { total, capped } = list;

  return (
    <>
      {list.error && <p className="mod-error" role="alert">{list.error}</p>}
      <div className="mod-filters">
        <label>Kind
          <select value={kind} onChange={(event) => setKind(event.target.value)}>
            {jobKinds.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label>Status
          <select value={status} onChange={(event) => setStatus(event.target.value)}>
            <option value="all">Any status</option>
            {jobStatuses.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
        </label>
        <label><span className="visually-hidden">Search</span>
          <input type="search" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder="Job title or who posted it"/>
        </label>
      </div>
      {rows.length === 0
        ? (!list.error && <p className="admin-empty">No jobs match.</p>)
        : (
          <div className="mod-table-wrap">
            <table className="mod-audit">
              <thead>
                <tr>
                  {header('job', 'Job')}{header('kind', 'Kind')}{header('postedBy', 'Posted by')}{header('area', 'Area')}{header('status', 'Status')}{header('bids', 'Bids')}
                  {header('posted', 'Posted')}{header('deadline', 'Deadline')}{header('awardedTo', 'Awarded to')}{header('work', 'Work')}{header('value', 'Value')}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={`${row.kind}-${row.id}`}>
                    <td>{orDash(row.title)}</td>
                    <td>{jobKindLabel(row.kind)}</td>
                    <td>
                      {row.poster_user_id
                        ? <button type="button" className="admin-link" onClick={() => onOpen({ tab: 'people', person: row.poster_user_id })}>{row.posted_by || 'No name given'}</button>
                        : orDash(row.posted_by)}
                    </td>
                    <td>{orDash(row.location)}</td>
                    <td>{jobStatusLabel(row.status)}</td>
                    <td>{count(row.bids)}</td>
                    <td>{formatDay(row.created_at)}</td>
                    <td>{formatDay(row.deadline)}</td>
                    <td>
                      {row.awarded_to_id && showTrades
                        ? <button type="button" className="admin-link" onClick={() => onOpen({ tab: 'trades', trade: row.awarded_to_id })}>{row.awarded_to || 'Trade company'}</button>
                        : (row.awarded_to_id ? row.awarded_to || 'Trade company' : orDash(row.awarded_to))}
                    </td>
                    <td>{workStatusLabel(row.work_status)}</td>
                    <td>{formatMoney(row.amount_minor, row.currency || 'GBP')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      {capped && <p className="mod-note">{`Showing the first ${count(listRowLimit)}.`}</p>}
    </>
  );
}
